import { useEffect, useMemo, useState } from 'react';
import type { Code } from '../shared/types';
import { CodeCard } from './components/CodeCard';
import { Controls, type Filter } from './components/Controls';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Notes } from './components/Notes';
import { Redeem } from './components/Redeem';
import { Toast } from './components/Toast';
import { useClipboard } from './hooks/useClipboard';
import { useCodes } from './hooks/useCodes';

/** How often relative timestamps are recomputed. */
const TICK_MS = 30_000;

export function App() {
  const { codes, loading, error, myVotes, vote, retry } = useCodes();
  const { copied, failed, copy } = useClipboard();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('working');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const live = useMemo(() => codes.filter(isLive), [codes]);

  const counts = useMemo<Record<Filter, number>>(
    () => ({ all: codes.length, working: live.length, dead: codes.length - live.length }),
    [codes, live]
  );

  const visible = useMemo(() => {
    const byFilter =
      filter === 'all' ? codes : filter === 'working' ? live : codes.filter((c) => !isLive(c));
    return byFilter.filter((code) => matches(code, query));
  }, [codes, live, filter, query]);

  return (
    <>
      <div className="shell">
        <Header />

        <main>
          <h1 className="page-title">Aniimo codes that work</h1>

          {/* Sticky search releases at the end of the list, not over the
              sections below it. */}
          <section>
            <Controls
              query={query}
              onQueryChange={setQuery}
              filter={filter}
              onFilterChange={setFilter}
              counts={counts}
            />

            {error ? (
              <div className="empty">
                <p>{error}</p>
                <button
                  type="button"
                  className="button button-primary"
                  style={{ marginTop: '1rem' }}
                  onClick={retry}
                >
                  Try again
                </button>
              </div>
            ) : loading ? (
              <ul className="code-list">
                {[0, 1, 2, 3].map((i) => (
                  <li className="skeleton" key={i} />
                ))}
              </ul>
            ) : visible.length === 0 ? (
              <div className="empty">Nothing matches that.</div>
            ) : (
              <ul className="code-list">
                {visible.map((code) => (
                  <CodeCard
                    key={code.id}
                    code={code}
                    now={now}
                    copied={copied === code.code}
                    myVote={myVotes[code.id]}
                    onCopy={(value) => void copy(value)}
                    onVote={(id, kind) => void vote(id, kind)}
                  />
                ))}
              </ul>
            )}
          </section>

          <Redeem />
          <Notes />
        </main>

        <Footer />
      </div>

      {copied && (
        <Toast
          failed={failed}
          message={
            failed
              ? 'Copying was blocked. Select the code and copy it by hand.'
              : copied.includes('\n')
                ? `Copied ${copied.split('\n').length} codes`
                : 'Copied'
          }
        />
      )}
    </>
  );
}

/** Live means we are not telling people it is dead. */
function isLive(code: Code): boolean {
  return code.status === 'active' && code.verdict !== 'broken';
}

function matches(code: Code, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    code.code.toLowerCase().includes(needle) ||
    code.rewards.some((reward) => reward.item.toLowerCase().includes(needle))
  );
}
